import { surfaceField } from "./_shared";
/**
 * Header section — locked. Always rendered at the top of every template;
 * cannot be removed, hidden, or reordered. Settings exposed here are the
 * minimal toggles a seller can adjust without breaking navigation.
 *
 * Navigation links are stored as `nav-link` blocks. Each block carries a
 * bilingual label and a URL/path. Sellers reorder + add/remove via the
 * inspector's block list.
 */
export const headerSchema = {
    type: "header",
    // The top bar of every page (desktop nav bar + the mobile app bar) and the
    // menu links. The mobile bottom bar is now its own `nav` section, so this is
    // just "Header" again (was briefly "Header & nav" while they were merged).
    name: { ar: "الهيدر", en: "Header" },
    description: {
        ar: "الشريط العلوي الثابت في كل صفحات المتجر — التنقل والبحث واللوجو.",
        en: "The fixed top bar on every storefront page — navigation, search, and logo.",
    },
    locked: true,
    criticalForSeo: true,
    maxPerTemplate: 1,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            // Desktop-only: the top navigation bar this styles is `hidden lg:block`
            // (BuyerHeader) — on phone/tablet the app-mode chrome (`mobileTopStyle` +
            // bottom nav) is what renders instead. Tagged so the simple editor hides
            // this control on the mobile/tablet tabs (it would otherwise look like a
            // no-op duplicate of "أعلى الموبايل" / Mobile top).
            appliesTo: ["desktop"],
            label: { ar: "شكل الشريط العلوي", en: "Header style" },
            helpText: {
                ar: "اختر شكل شريط التنقل. كل نوع ليه مظهر مختلف.",
                en: "Pick the navigation bar design. Each type has its own look.",
            },
            options: [
                { value: "bar", label: { ar: "كلاسيكي", en: "Classic bar" } },
                { value: "pill", label: { ar: "عائم", en: "Floating pill" } },
                { value: "minimal", label: { ar: "بسيط", en: "Minimal" } },
                { value: "editorial", label: { ar: "تحريري", en: "Editorial" } },
                {
                    value: "shopmenu",
                    label: { ar: "قائمة وبحث", en: "Shop menu + search" },
                },
                // ── EasyOrders header archetype ports (2026-07) ──────────────────
                // Measured full-style ports of the EO storefront headers, the header
                // equivalent of the footer archetype set. Each reproduces a source
                // family's real STYLE (height, logo treatment, nav typography/case/
                // tracking, icon cluster, and — for `search-prime` — a multi-row
                // search-led structure), not just the content arrangement. Ground
                // colour still comes from the `surface` slot + `transparentOverHero`
                // below, so a port can be transparent-over-hero or solid.
                {
                    // nav-left · centered wordmark · icons-right; fine uppercase,
                    // letter-spaced nav; medium height. pearl / peral / luxury /
                    // avyro / elite / sharp.
                    value: "luxe-centered",
                    label: { ar: "لوجو بالنص فخم", en: "Luxe centered" },
                },
                {
                    // Same split layout but TALL, big logo, bold uppercase nav.
                    // dark / volta / blast.
                    value: "grand-centered",
                    label: { ar: "لوجو بالنص كبير", en: "Grand centered" },
                },
                {
                    // logo-left · centered nav · icons-right; solid ground; medium
                    // weight nav. nature / hype.
                    value: "aisle",
                    label: { ar: "ممر", en: "Aisle" },
                },
                {
                    // Multi-row search-led header: logo + big search field + cart, then
                    // a nav row beneath. prime.
                    value: "search-prime",
                    label: { ar: "بحث بصفين", en: "Search bar" },
                },
                {
                    // `ab-header` — a rounded RECTANGLE card (not a pill) inset 20px from
                    // the page edge, 48px tall, floating over the hero and scrolling away
                    // with it. Wordmark at the start, small uppercase tracked nav
                    // centred, icons at the end. phoenix.
                    value: "floating-card",
                    label: { ar: "بطاقة عائمة", en: "Floating card" },
                },
            ],
            default: "bar",
        },
        // Header background — the desktop bar's colour, DECOUPLED from `variant`.
        // Was hardwired to `--store-accent` with contrast guessed from the variant
        // name (`onDark = variant !== 'minimal'`); now the seller picks a surface
        // slot and the buyer derives readable text. Defaults to Accent so the
        // long-standing bold-bar look is unchanged. Desktop-only: the mobile app bar
        // has its own `mobileTopBackground`. (Minimal stays transparent; the
        // transparent-over-hero toggle still overrides this while over the hero.)
        {
            ...surfaceField("accent"),
            appliesTo: ["desktop"],
            label: { ar: "خلفية الهيدر", en: "Header background" },
        },
        // Cart-trigger glyph. EO uses a different bag/cart shape per storefront
        // engine, so the family picks one; the buyer's `BuyerCartIcon` renders the
        // matching glyph via the header's `CartIconProvider`.
        {
            id: "cartIcon",
            type: "select",
            ui: "thumbnail",
            label: { ar: "أيقونة السلة", en: "Cart icon" },
            helpText: {
                ar: "شكل أيقونة السلة في الهيدر.",
                en: "The shape of the cart icon in the header.",
            },
            options: [
                { value: "bag", label: { ar: "شنطة", en: "Bag" } },
                { value: "cart", label: { ar: "عربة", en: "Cart" } },
                { value: "basket", label: { ar: "سلة", en: "Basket" } },
                { value: "tote", label: { ar: "توت", en: "Tote" } },
                { value: "handbag", label: { ar: "شنطة يد", en: "Handbag" } },
            ],
            default: "bag",
        },
        // Wishlist UI — the heart on product cards + the header wishlist icon.
        // OFF by default (EO shows it on one engine only); the wishlist page +
        // recently-viewed still work regardless, this only toggles the on-card and
        // header affordances.
        {
            id: "showWishlist",
            type: "checkbox",
            label: { ar: "تفعيل المفضلة (قلب المنتج)", en: "Enable wishlist (product heart)" },
            helpText: {
                ar: "يعرض قلب المفضلة على كروت المنتجات وأيقونة المفضلة في الهيدر.",
                en: "Shows the wishlist heart on product cards and a wishlist icon in the header.",
            },
            default: false,
        },
        // Quick-view — a compact preview modal opened from a card icon.
        {
            id: "showQuickView",
            type: "checkbox",
            label: { ar: "معاينة سريعة على الكروت", en: "Quick view on cards" },
            helpText: {
                ar: "يضيف زر معاينة سريعة على كروت المنتجات يفتح نافذة بمعلومات المنتج.",
                en: "Adds a quick-view button on product cards that opens a preview modal.",
            },
            default: false,
        },
        // Nav-link hover treatment. `fade` keeps the platform's long-standing
        // opacity dim; the rest are EO-style motion (underline slide, pill, accent).
        {
            id: "navHover",
            type: "select",
            appliesTo: ["desktop"],
            label: { ar: "تأثير مرور الماوس على الروابط", en: "Nav link hover" },
            helpText: {
                ar: "شكل تأثير مرور الماوس على روابط القائمة العلوية.",
                en: "How the top-nav links react on hover.",
            },
            options: [
                { value: "fade", label: { ar: "تعتيم", en: "Fade" } },
                { value: "underline", label: { ar: "خط سفلي", en: "Underline" } },
                {
                    value: "underline-slide",
                    label: { ar: "خط سفلي منزلق", en: "Underline slide" },
                },
                { value: "highlight", label: { ar: "تظليل", en: "Highlight pill" } },
                { value: "accent", label: { ar: "لون مميّز", en: "Accent color" } },
            ],
            default: "fade",
        },
        // NOTE: the mobile bottom bar (`bottomNav`) AND the cart-row style
        // (`cartRowStyle`) both moved to the `nav` section (see sections/nav.ts) —
        // the cart lives in the bottom bar, so its item-removal style belongs there.
        // Both are migrated off the header by `shellHydration` and read via the
        // shared resolvers in `apps/buyer/src/lib/bottomNav.ts`, which still fall
        // back to the legacy `header.*` values on drafts not yet re-saved.
        // ORDER MATTERS: `logoDisplay` (always visible) is authored BEFORE the
        // `showIf`-gated `logoPosition` on purpose. The Layout step renders unlisted
        // fields in schema-authoring order (`LAYOUT_ORDER_PRIORITY` only pins
        // variant/spacing/scale/align), so putting the conditional field LAST means
        // it appears/disappears below a stable row instead of shoving `logoDisplay`
        // up and down as the seller switches variants.
        {
            id: "logoDisplay",
            type: "select",
            // The top header is `hidden lg:block` (desktop-only) whenever a mobile
            // bottom bar is active (BuyerHeader.tsx) — the common setup — so its
            // controls only affect the desktop view in practice. Marked desktop so
            // the editor collapses them on the mobile tab. (For a store with NO bottom
            // bar the top header does show on mobile; apply-everywhere still applies
            // the value there, it's just edited from the desktop tab.)
            appliesTo: ["desktop"],
            label: { ar: "عرض الشعار", en: "Logo display" },
            helpText: {
                ar: "إظهار الشعار والاسم معًا، أم الاسم فقط، أم الشعار فقط.",
                en: "Show the logo and name, the name only, or the logo only.",
            },
            options: [
                { value: "both", label: { ar: "الشعار والاسم", en: "Logo and name" } },
                { value: "name", label: { ar: "الاسم فقط", en: "Name only" } },
                { value: "logo", label: { ar: "الشعار فقط", en: "Logo only" } },
            ],
            default: "both",
        },
        {
            id: "logoPosition",
            type: "select",
            // Only the desktop bar centers the logo (the centered pill is `md:flex`);
            // the mobile header uses a fixed logo layout, so this has no mobile effect.
            appliesTo: ["desktop"],
            // Only the classic `bar` variant reads `logoPosition` (BuyerHeader.tsx
            // `centeredLogo`). pill/minimal/shopmenu ignore it and editorial is always
            // centered, so the control would be a dead no-op there — gate it to `bar`.
            // See docs/editor-control-interactions.md.
            showIf: { variant: "bar" },
            label: { ar: "موضع الشعار", en: "Logo position" },
            options: [
                { value: "left", label: { ar: "اليسار", en: "Left" } },
                { value: "center", label: { ar: "الوسط", en: "Center" } },
            ],
            default: "left",
        },
        {
            id: "sticky",
            type: "checkbox",
            // Desktop-only in practice — see the note on `logoDisplay` above.
            appliesTo: ["desktop"],
            label: { ar: "تثبيت الشريط لما تنزل", en: "Sticky on scroll" },
            default: true,
        },
        {
            id: "scrollBehavior",
            type: "select",
            label: { ar: "سلوك الهيدر عند النزول", en: "On scroll" },
            helpText: {
                ar: "يفضل ثابت، أو يختفي وأنت نازل ويرجع أول ما تطلع.",
                en: "Keep it pinned, or hide it as the visitor scrolls down and bring it back on scroll up.",
            },
            options: [
                { value: "pinned", label: { ar: "ثابت", en: "Stay pinned" } },
                {
                    value: "hide",
                    label: { ar: "يختفي وينزل", en: "Hide on scroll down" },
                },
            ],
            default: "pinned",
            showIf: { sticky: true },
        },
        {
            id: "solidOnScroll",
            type: "checkbox",
            label: {
                ar: "يتحول للون كامل عند النزول",
                en: "Solid background on scroll",
            },
            helpText: {
                ar: "الهيدر الشفاف يرجع بلون كامل بعد ما الصفحة تنزل.",
                en: "A transparent header re-solidifies once the page scrolls.",
            },
            default: true,
        },
        {
            id: "shadowOnScroll",
            type: "checkbox",
            label: { ar: "ظل عند النزول", en: "Shadow on scroll" },
            helpText: {
                ar: "ظل خفيف يفصل الهيدر عن المحتوى بعد النزول.",
                en: "A soft shadow separates the header from the page once scrolled.",
            },
            default: false,
        },
        // (Removed the `showDivider` "Section divider line" control: it only ever
        // applied to the `minimal` variant, rendered a 1px `--hairline` that's barely
        // visible on a transparent-over-hero header, and the editor selection outline
        // sat on that exact edge — low value, dropped. See sections/_shared.ts.)
        // ── Mobile app-mode top bar — COMPOSABLE (2026-07) ──────────────────
        // The old `mobileTopStyle` (pill/icons/welcome) + `showMobileSearch`
        // checkbox were replaced with independent component toggles: the seller
        // turns on the parts they want and `renderMobileAppBar` (BuyerHeader.tsx)
        // arranges them. All OFF → nothing renders (the "None" top bar). The whole
        // top bar is `lg:hidden`, so every `mobile*` field is phone/tablet only.
        // Migrated from the old fields by `shellHydration`.
        {
            id: "mobileShowSearch",
            type: "checkbox",
            appliesTo: ["mobile", "tablet"],
            label: { ar: "شريط البحث", en: "Search bar" },
            helpText: {
                ar: "شريط بحث أعلى الصفحة الرئيسية على الموبايل.",
                en: "A search bar at the top of the mobile home.",
            },
            default: true,
        },
        {
            id: "mobileShowMenu",
            type: "checkbox",
            appliesTo: ["mobile", "tablet"],
            label: { ar: "زر القائمة", en: "Menu button" },
            helpText: {
                ar: "زر القائمة (☰) اللي بيفتح روابط التنقل وتغيير اللغة.",
                en: "The menu (☰) button that opens the nav links and language switch.",
            },
            default: true,
        },
        {
            id: "mobileShowLogo",
            type: "checkbox",
            appliesTo: ["mobile", "tablet"],
            label: { ar: "شعار المتجر", en: "Store logo" },
            helpText: {
                ar: "إظهار شعار المتجر أعلى الصفحة على الموبايل.",
                en: "Show the store logo at the top of the mobile home.",
            },
            default: false,
        },
        {
            id: "mobileShowGreeting",
            type: "checkbox",
            appliesTo: ["mobile", "tablet"],
            label: { ar: "رسالة ترحيب", en: "Welcome greeting" },
            helpText: {
                ar: "سطر ترحيب + اسم المتجر أعلى الصفحة، زي تطبيقات التسوق.",
                en: "A greeting line + store name at the top, like native shopping apps.",
            },
            default: false,
        },
        // Custom greeting line (store name renders bold underneath). Empty →
        // built-in default ("Welcome 👋" / "أهلاً بك 👋"). Shown once the greeting
        // toggle is on.
        {
            id: "mobileGreeting",
            type: "text",
            i18n: true,
            appliesTo: ["mobile", "tablet"],
            label: { ar: "نص الترحيب", en: "Greeting text" },
            validate: { max: 60 },
            showIf: { mobileShowGreeting: true },
        },
        // Bold line under the greeting. Empty → the store name.
        {
            id: "mobileTitle",
            type: "text",
            i18n: true,
            appliesTo: ["mobile", "tablet"],
            label: { ar: "عنوان الترحيب", en: "Welcome title" },
            helpText: {
                ar: "السطر العريض تحت الترحيب. سيبه فاضي عشان يظهر اسم المتجر.",
                en: "The bold line under the greeting. Leave empty to show the store name.",
            },
            validate: { max: 60 },
            showIf: { mobileShowGreeting: true },
        },
        // Background of the whole app-mode top area (mobile/tablet). Its OWN slot,
        // separate from the desktop `surface` above, defaulting to `page` so the
        // mobile top keeps its light "native app" look (sits on the page background)
        // until the seller picks a colour. Was the raw `mobileTopBackground` colour.
        {
            ...surfaceField("page"),
            id: "mobileSurface",
            appliesTo: ["mobile", "tablet"],
            label: { ar: "خلفية أعلى الموبايل", en: "Mobile top background" },
        },
        {
            id: "transparentOverHero",
            type: "checkbox",
            // Desktop-only in practice — see the note on `logoDisplay` above.
            appliesTo: ["desktop"],
            // Offered for every variant that draws a background this can remove:
            // bar/editorial/shopmenu (solid band) and `pill` (the band is transparent
            // but the PILL BODY is solid accent — it now honors this toggle too, so it
            // is no longer the no-op it used to be).
            // `minimal` is EXCLUDED on purpose, not by oversight: it has no background
            // over the hero by design (transparent until you scroll, then frosted
            // glass), so this toggle is permanently "on" for it — exposing it would be
            // a dead control. See docs/editor-control-interactions.md.
            // The two centered-logo ports also draw a solid band this can remove, so
            // they float transparent over the hero like the classic bar/pill. `aisle`
            // and `search-prime` are always-solid archetypes (their whole look is the
            // ground colour + structure), so they stay off the list.
            showIf: {
                variant: [
                    "bar",
                    "editorial",
                    "shopmenu",
                    "pill",
                    "luxe-centered",
                    "grand-centered",
                ],
            },
            label: {
                ar: "شفافة فوق الواجهة الرئيسية",
                en: "Transparent over hero",
            },
            helpText: {
                ar: "بتبقى خلفية الشريط العلوي شفافة عشان الواجهة الرئيسية تبان من وراها.",
                en: "Makes the header background transparent so the hero shows through.",
            },
            default: false,
        },
    ],
    blocks: [
        {
            type: "nav-link",
            name: { ar: "رابط في القائمة", en: "Navigation link" },
            description: {
                ar: "رابط واحد في القائمة العلوية.",
                en: "A single link in the top navigation.",
            },
            maxPerSection: 8,
            settings: [
                {
                    id: "label",
                    type: "text",
                    i18n: true,
                    label: { ar: "النص", en: "Label" },
                    // ponytail: no max — a nav-link label mirrors a category name, which
                    // is unbounded (category DTO is MinLength(2) only). Add a max back if
                    // the header layout ever needs to bound it.
                    validate: { required: true },
                },
                // `target` chooses the KIND of nav link. Two seller-facing types:
                //  - `category` → opens a category's products page (picked via
                //    `categoryRef`, a single categoryPicker).
                //  - `section`  → in-page anchor that scrolls to a home section
                //    (picked via `sectionRef` from the live home layout in the editor).
                // `url` is a RETIRED legacy type (a free path/URL in `href`). It is kept
                // ONLY as a `hidden` option so pre-existing `url` nav-links stay valid at
                // publish and keep rendering — the editor no longer offers it, so new
                // links are always category/section.
                {
                    id: "target",
                    type: "select",
                    label: { ar: "نوع الرابط", en: "Link type" },
                    options: [
                        { value: "category", label: { ar: "فئة", en: "Category" } },
                        {
                            value: "section",
                            label: { ar: "قسم في الصفحة الرئيسية", en: "Section on home" },
                        },
                        {
                            value: "url",
                            label: { ar: "صفحة أو رابط", en: "Page or URL" },
                            hidden: true,
                        },
                    ],
                    default: "category",
                },
                {
                    id: "categoryRef",
                    type: "categoryPicker",
                    single: true,
                    label: { ar: "الفئة", en: "Category" },
                    helpText: {
                        ar: "اختر الفئة اللي الرابط هيفتحها — الزبون هيشوف منتجات الفئة دي.",
                        en: "Pick the category this link opens — buyers land on that category’s products.",
                    },
                    default: "",
                    showIf: { target: "category" },
                },
                {
                    id: "sectionRef",
                    type: "text",
                    label: { ar: "القسم", en: "Section" },
                    helpText: {
                        ar: "بتختاره من قائمة أقسام الصفحة الرئيسية في المحرر.",
                        en: "Pick from the home page sections in the editor.",
                    },
                    validate: { max: 120 },
                    showIf: { target: "section" },
                },
                // Legacy free path/URL — only shown for pre-existing `url` nav-links
                // (the `url` target is hidden from the editor, so new links never set it).
                {
                    id: "href",
                    type: "text",
                    label: { ar: "الرابط", en: "URL or path" },
                    helpText: {
                        ar: "مثل /shop أو رابط كامل https://…",
                        en: "e.g. /shop or a full https://… URL",
                    },
                    validate: { max: 240 },
                    default: "/",
                    showIf: { target: "url" },
                },
            ],
        },
    ],
};
//# sourceMappingURL=header.js.map